import CloseIcon from "@assets/svg/CloseIcon";
// @ts-ignore
import ProfileImg from "@assets/profilePic1.png";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@components/ui/dialog";
import { Label } from "@components/ui/label";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";

import { Dispatch, SetStateAction, useState } from "react";
import { useSnackbar } from "notistack";
import { jobAddTracker } from "@endpoints/job.service";
import { EnumJobTrackerStatus } from "@utils/constants";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

interface SubmitToHiringCompanyModalProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  jobApplicationId: string;
  onHiringCompanySubmit: () => void;
  onViewSubmissionQueue: () => void;
}

const options = [
  {
    label: "Send Now",
    desc: "Send this candidate’s profile immediately to the hiring company.",
    value: "1",
  },
  {
    label: "View More Applicants",
    desc: "Add this candidate to queue so you can review more applicants and submit them together.",
    value: "2",
  },
];

const SubmitToHiringCompanyModal = ({
  openModal,
  setOpenModal,
  jobApplicationId,
  onHiringCompanySubmit,
  onViewSubmissionQueue
}: SubmitToHiringCompanyModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedOption, setSelectedOption] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleShowSubmissionQueue = () => {
    setOpenModal(false)
    onViewSubmissionQueue()
  };

  const handleContinue = async () => {
    if (!selectedOption)
      return enqueueSnackbar("You must select one option.", {
        variant: "error",
      });
    if (!jobApplicationId)
      return enqueueSnackbar("Job application id is required.", {
        variant: "error",
      });
    try {
      setIsLoading(true);
      const payload = {
        jobApplicationId,
        status: EnumJobTrackerStatus.SUBMIT_TO_HIRING_COMPANY,
        addInSubmissionQueue: selectedOption === "2",
      };
      const { success, data } = await jobAddTracker(payload);
      if (success) {
        if (selectedOption !== "2") onHiringCompanySubmit();
        enqueueSnackbar(
          selectedOption === "2"
            ? "Added to submission queue successfully."
            : "Submitted to hiring company successfully.",
          { variant: "success" }
        );
      } else if (data?.message) {
        enqueueSnackbar(data?.message, { variant: "error" });
      }
      setOpenModal(false);
      // console.log('this is response for job add tracker',response)
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={openModal} onOpenChange={setOpenModal}>
      <DialogContent showCloseButton={false} className="bg-white">
        <DialogClose asChild>
          <button
            onClick={() => {
              setOpenModal(false);
            }}
            className="h-max ml-auto rounded-md hover:bg-gray-50 absolute right-3 top-3"
          >
            <CloseIcon className={"w-5 h-5"} />
          </button>
        </DialogClose>
        <DialogTitle className="flex flex-col items-center gap-2">
          <span className="text-lg font-semibold text-primary">
            Submit Candidate
          </span>
          <span className="text-xs font-medium text-light-black text-center">
            How would you like to proceed with this candidate?
          </span>
        </DialogTitle>
        <RadioGroup
          onValueChange={(value) => setSelectedOption(value)}
          className="gap-8 my-6"
        >
          {options?.map((option) => {
            return (
              <div key={option?.value} className="flex gap-3">
                <RadioGroupItem value={option?.value} id={option?.value} />

                <div className="flex flex-col gap-3 pr-10">
                  <label
                    htmlFor={option?.value}
                    className="flex gap-3 pr-10 cursor-pointer"
                  >
                    <div className="flex flex-col gap-2">
                      <span className="text-xs font-medium text-primary">
                        {option?.label}
                      </span>
                      <span className="text-xs font-medium text-light-black">
                        {option?.desc}
                      </span>
                    </div>
                  </label>
                  {option?.value === "2" ? (
                    <span
                      onClick={handleShowSubmissionQueue}
                      className="text-xs font-semibold italic text-light-black underline cursor-pointer hover:text-blue-500"
                    >
                      View submission queue
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </RadioGroup>
        <DialogFooter>
          <button
            onClick={() => setOpenModal(false)}
            className="text-xs font-semibold text-primary bg-gray-200 rounded-md h-8 px-4 hover:bg-gray-300"
          >
            Back
          </button>
          <button
            onClick={handleContinue}
            className="flex items-center gap-2 text-xs font-semibold text-white bg-primary rounded-md h-8 px-4 hover:bg-black"
          >
            Continue
            {isLoading ? <LoadingAnimation /> : null}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitToHiringCompanyModal;
